import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaComments,
  FaTimes,
  FaMicrophone,
  FaWhatsapp,
  FaPaperPlane,
  FaRobot,
} from "react-icons/fa";
import servicesService from "./services/servicesService";
import settingsService from "./services/settingsService";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [messages, setMessages] = useState([
    {
      text: "Hi 👋 Welcome to VJ Dsign Studio! Looking to transform your space? Tell me what you're looking for!",
      bot: true,
      time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [servicesCatalog, setServicesCatalog] = useState([]);
  const [phoneNumber, setPhoneNumber] = useState("918754545419");

  const endRef = useRef();

  useEffect(() => {
    async function loadData() {
      try {
        const [services, allSettings, general] = await Promise.all([
          servicesService.getActive(),
          settingsService.getAll(),
          settingsService.getSetting("general"),
        ]);

        if (services && services.length > 0) {
          setServicesCatalog(
            services.map((s) => ({
              id: s.id,
              name: s.name,
              category: (s.slug || s.name || "").toLowerCase(),
              desc: s.description || "Customized interior design and architectural solution.",
            }))
          );
        }

        const raw = (allSettings?.whatsapp_number || general?.whatsapp || allSettings?.phone || general?.phone_raw || "").replace(/[^0-9]/g, "");
        if (raw) {
          setPhoneNumber(raw);
        }
      } catch (err) {
        // Safe fallback
      }
    }
    loadData();
  }, []);

  const whatsappLink = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
    "Hi VJ Dsign Studio, I am looking for interior design services."
  )}`;

  useEffect(() => {
    const handleScroll = () => setVisible(window.scrollY > 150);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const startListening = () => {
    const SpeechRecognition =
      window.webkitSpeechRecognition || window.SpeechRecognition;
    if (!SpeechRecognition) return alert("Voice recognition not supported in this browser.");

    const recognition = new SpeechRecognition();
    recognition.lang = "en-IN";
    recognition.start();
    setListening(true);

    recognition.onresult = (e) => {
      setInput(e.results[0][0].transcript);
      setListening(false);
    };

    recognition.onerror = () => setListening(false);
  };

  const getReply = (msg) => {
    const text = msg.toLowerCase();

    // Match matching services from Supabase
    const matched = servicesCatalog.filter((s) =>
      text.includes(s.name.toLowerCase()) ||
      s.name.toLowerCase().split(" ").some((w) => w.length > 3 && text.includes(w))
    );

    if (matched.length > 0) {
      return {
        type: "services",
        items: matched,
      };
    }

    if (text.includes("residential") || text.includes("home") || text.includes("kitchen") || text.includes("wardrobe") || text.includes("living")) {
      const resServices = servicesCatalog.filter((s) =>
        s.category.includes("residential") || s.category.includes("kitchen") || s.category.includes("wardrobe") || s.category.includes("pooja") || s.category.includes("tv")
      );
      return {
        type: "services",
        items: resServices.length > 0 ? resServices : servicesCatalog.slice(0, 3),
      };
    }

    if (text.includes("commercial") || text.includes("office") || text.includes("shop") || text.includes("reception") || text.includes("workstation")) {
      const comServices = servicesCatalog.filter((s) => s.category.includes("commercial") || s.category.includes("office"));
      return {
        type: "services",
        items: comServices.length > 0 ? comServices : servicesCatalog.slice(0, 3),
      };
    }

    if (text.includes("renovation") || text.includes("remodel") || text.includes("alteration") || text.includes("change")) {
      const renServices = servicesCatalog.filter((s) => s.category.includes("renovat"));
      return {
        type: "services",
        items: renServices.length > 0 ? renServices : servicesCatalog.slice(0, 3),
      };
    }

    if (text.includes("price") || text.includes("cost") || text.includes("budget") || text.includes("quote") || text.includes("package")) {
      return {
        type: "text",
        text: "Our design quotes vary depending on floor dimensions and material selection. Check our 'Packages' section or click 'Enquire' to discuss your floor plan directly with our designer! ✨",
      };
    }

    if (text.includes("agent") || text.includes("whatsapp") || text.includes("call") || text.includes("contact")) {
      return { type: "action", text: "Connecting you directly with a designer at VJ Dsign Studio..." };
    }

    return {
      type: "text",
      text: "Please choose or type a service you are looking for: Residential, Commercial, or Renovation 👇",
    };
  };

  const handleSend = (customText) => {
    const msgToSend = typeof customText === "string" ? customText : input;
    if (!msgToSend.trim()) return;

    const time = new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    setMessages((prev) => [...prev, { text: msgToSend, bot: false, time }]);
    setInput("");
    setLoading(true);

    setTimeout(() => {
      const reply = getReply(msgToSend);

      if (reply.type === "text") {
        setMessages((prev) => [...prev, { text: reply.text, bot: true, time }]);
      }

      if (reply.type === "services") {
        setMessages((prev) => [
          ...prev,
          { text: "Here is what we offer under that category: 👇", bot: true, time },
          { services: reply.items, bot: true, time },
        ]);
      }

      if (reply.type === "action") {
        setMessages((prev) => [...prev, { text: reply.text, bot: true, time }]);
        setTimeout(() => window.open(whatsappLink, "_blank"), 1200);
      }

      setLoading(false);
    }, 700);
  };

  return (
    <div className="fixed bottom-6 right-6 z-[9999] flex flex-col items-end gap-3">
      
      {/* CHAT WINDOW */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            className="w-[330px] h-[490px] bg-white rounded-3xl shadow-2xl flex flex-col overflow-hidden border border-slate-100 mb-2"
          >
            {/* HEADER */}
            <div className="bg-[#9BB7A3] text-white p-4 flex justify-between items-center shadow-sm">
              <div className="flex items-center gap-2">
                <FaRobot className="text-lg" />
                <div className="flex flex-col">
                  <span className="font-bold text-sm tracking-wide">VJ Design Assistant</span>
                  <span className="text-[10px] text-white/80">Online | Ready to Design</span>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="hover:opacity-80 transition-opacity">
                <FaTimes />
              </button>
            </div>

            {/* QUICK CHIPS */}
            <div className="flex gap-2 p-2 overflow-x-auto bg-slate-50 border-b scrollbar-none">
              {["Residential", "Commercial", "Renovation", "Packages"].map((item) => (
                <button
                  key={item}
                  onClick={() => handleSend(item)}
                  className="text-xs font-semibold bg-[#9BB7A3]/10 text-[#7d9b85] border border-[#9BB7A3]/20 px-3 py-1.5 rounded-full whitespace-nowrap hover:bg-[#9BB7A3] hover:text-white transition-all duration-200"
                >
                  {item}
                </button>
              ))}
            </div>

            {/* MESSAGES */}
            <div className="flex-1 overflow-y-auto p-3 bg-slate-50/50 space-y-3">
              {messages.map((m, i) => (
                <div key={i} className={`flex flex-col ${m.bot ? "items-start" : "items-end"}`}>
                  {m.text && (
                    <div
                      className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed shadow-sm ${
                        m.bot 
                          ? "bg-white text-slate-800 rounded-tl-sm border border-slate-100" 
                          : "bg-[#9BB7A3] text-white rounded-tr-sm"
                      }`}
                    >
                      {m.text}
                    </div>
                  )}

                  {m.services && (
                    <div className="flex flex-col gap-2 max-w-[85%] mt-1">
                      {m.services.map((s, idx) => (
                        <div key={idx} className="bg-white p-3 rounded-2xl shadow-sm border border-slate-100 transition-all hover:shadow-md">
                          <h4 className="font-bold text-slate-800 text-sm">{s.name}</h4>
                          <p className="text-xs text-slate-500 mt-1 leading-normal line-clamp-2">{s.desc}</p>
                          <div className="flex justify-end items-center mt-3 pt-2 border-t border-slate-50">
                            <button
                              onClick={() => {
                                const customWhatapp = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(
                                  `Hi VJ Dsign Studio, I am interested in your "${s.name}" interior service. Please share details.`
                                )}`;
                                window.open(customWhatapp, "_blank");
                              }}
                              className="text-xs font-bold bg-[#9BB7A3] text-white px-3 py-1.5 rounded-xl hover:bg-[#86a38e] transition-colors"
                            >
                              Enquire Now
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <span className="text-[9px] text-slate-400 mt-0.5 px-1">{m.time}</span>
                </div>
              ))}

              {loading && (
                <div className="flex items-center gap-1.5 text-xs text-slate-400 bg-white border px-3 py-2 rounded-xl w-max shadow-sm">
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" />
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:0.2s]" />
                  <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:0.4s]" />
                </div>
              )}
              <div ref={endRef} />
            </div>

            {/* INPUT */}
            <div className="p-3 border-t bg-white flex items-center gap-2">
              <input
                className="flex-1 border border-slate-200 bg-slate-50 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#9BB7A3] focus:bg-white transition-all"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSend()}
                placeholder="Ask about designs, pricing..."
              />

              <button 
                onClick={startListening} 
                className={`p-2 rounded-lg hover:bg-slate-100 transition-colors ${listening ? "text-red-500 animate-pulse" : "text-slate-400"}`}
                title="Voice Search"
              >
                <FaMicrophone size={16} />
              </button>

              <button 
                onClick={() => handleSend()} 
                className="p-2 bg-[#9BB7A3] text-white rounded-xl hover:bg-[#86a38e] transition-colors shadow-sm"
              >
                <FaPaperPlane size={14} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* FLOATING ACTION BUTTONS */}
      <div className="flex flex-col gap-3 items-end">
        <button
          onClick={() => setOpen(!open)}
          className="bg-[#9BB7A3] text-white w-14 h-14 rounded-full flex items-center justify-center shadow-xl hover:bg-[#86a38e] transition-all transform hover:scale-105 active:scale-95"
        >
          {open ? <FaTimes size={20} /> : <FaComments size={22} />}
        </button>

        {visible && (
          <a 
            href={whatsappLink} 
            target="_blank" 
            rel="noreferrer"
            className="transform transition-all duration-300 animate-fade-in"
          >
            <div className="bg-green-500 text-white w-14 h-14 rounded-full flex items-center justify-center shadow-xl hover:bg-green-600 transition-all transform hover:scale-105 active:scale-95">
              <FaWhatsapp size={26} />
            </div>
          </a>
        )}
      </div>
    </div>
  );
}